import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchQuizzes,
  postUserAnswer,
  resetUserAnswersState,
  setSelectedQuiz,
} from "../store/reducers/quizzesSlice";
import {
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";

const QuizDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const { quizzes, selectedQuiz, userAnswers } = useSelector(
    (state) => state.quizzes
  );

  const [answers, setAnswers] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const pendingNavigation = useRef(null);
  const unblockRef = useRef(null);

  const organizationId = localStorage.getItem("organisation_id");

  useEffect(() => {
    if (selectedQuiz && selectedQuiz.id === id) {
      setQuizData(selectedQuiz);
    } else if (quizzes.data.length > 0) {
      const found = quizzes.data.find((q) => q.id === id);
      if (found) {
        setQuizData(found);
        dispatch(setSelectedQuiz(found));
      }
    } else {
      dispatch(fetchQuizzes(organizationId));
    }
  }, [id, selectedQuiz, quizzes.data, dispatch, organizationId]);

  useEffect(() => {
    if (!quizData) return;

    unblockRef.current = history.block((location) => {
      const isIncomplete =
        Object.keys(answers).length !== quizData.questions.length;

      if (isIncomplete) {
        setOpenDialog(true);
        pendingNavigation.current = location; // store full location
        return false;
      }

      return true;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
      }
    };
  }, [history, answers, quizData]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (
        quizData &&
        Object.keys(answers).length !== quizData.questions.length
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [answers, quizData]);

  if (quizzes.loading || !quizData) {
    return <CircularProgress />;
  }

  const handleChange = (qId, optionId, isMulti) => {
    setAnswers((prev) => {
      if (!isMulti) {
        return { ...prev, [qId]: [optionId] };
      }

      const prevOptions = prev[qId] || [];

      if (prevOptions.includes(optionId)) {
        return {
          ...prev,
          [qId]: prevOptions.filter((id) => id !== optionId),
        };
      } else {
        return {
          ...prev,
          [qId]: [...prevOptions, optionId],
        };
      }
    });
  };

  const handleBackClick = () => {
    const isIncomplete =
      Object.keys(answers).length !== quizData.questions.length;

    if (isIncomplete) {
      setOpenDialog(true);
    } else {
      history.push("/quizzes");
    }
  };

  const handleConfirmLeave = () => {
    setOpenDialog(false);

    if (unblockRef.current) {
      unblockRef.current();
    }

    if (pendingNavigation.current) {
      history.push(pendingNavigation.current);
    } else {
      history.push("/quizzes");
    }
  };

  const handleCancelLeave = () => {
    setOpenDialog(false);
    pendingNavigation.current = null;
  };

  console.log(quizData)

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== quizData.questions.length) {
      toast.error("Please answer all questions");
      return;
    }

    dispatch(resetUserAnswersState());

    const formattedAnswers = Object.keys(answers).map((qId) => ({
      question_id: Number(qId),
      ids: answers[qId],
    }));

    let payload = {
      organization_id: +organizationId,
      answers: formattedAnswers,
    };


    if (quizData.video_id) {
      payload.video_id = quizData.video_id;
    } else if (quizData.card_id) {
      payload.card_id = quizData.card_id;
    }
    else if (quizData.quiz_id) {
      payload.quiz_id = quizData.quiz_id;
    }

    const response = await dispatch(postUserAnswer(payload));

    if (postUserAnswer.rejected.match(response)) {
      toast.error(
        response.payload?.error ||
          response.payload?.message ||
          "Quiz submission failed"
      );
      return;
    }

    const message = response.payload?.message || "Quiz submitted successfully";

    if (message.toLowerCase().includes("fail")) {
      toast.error(message);
    } else {
      toast.success(message);
    }


    if (unblockRef.current) {
      unblockRef.current();
    }

    history.push("/quizzes");
  };

  return (
    <div style={{ height: "93vh", overflow: "scroll", marginTop: "10px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBackClick}
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <FaArrowLeft size={14} />
          Back
        </Button>

        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {quizData.displayName}
        </Typography>
      </div>

      {quizData.questions?.map((q, index) => (
        <Card key={q.id} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">
              Q{index + 1}: {q.question}
            </Typography>

            <Typography variant="body2" mb={2}>
              DK: {q.question_dk || "-"}
            </Typography>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {q.correct_answers === 1 ? (
                <RadioGroup
                  value={answers[q.id]?.[0] || ""}
                  onChange={(e) =>
                    handleChange(q.id, Number(e.target.value), false)
                  }
                >
                  {q.options?.map((opt) => (
                    <FormControlLabel
                      key={opt.id}
                      value={opt.id}
                      control={<Radio />}
                      label={`${opt.text_en} / ${opt.text_dk || "-"}`}
                    />
                  ))}
                </RadioGroup>
              ) : (
                q.options?.map((opt) => (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        checked={answers[q.id]?.includes(opt.id) || false}
                        onChange={() => handleChange(q.id, opt.id, true)}
                      />
                    }
                    label={`${opt.text_en} / ${opt.text_dk || "-"}`}
                  />
                ))
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      <button
        className="btn px-4 bg_color mb-3"
        onClick={handleSubmit}
        disabled={userAnswers.loading}
      >
        {userAnswers.loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" />
            Submitting...
          </>
        ) : (
          "Submit Quiz"
        )}
      </button>


      <Dialog open={openDialog} onClose={handleCancelLeave}>
        <DialogTitle>Leave Quiz?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to leave the quiz? Your progress will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave}>No</Button>
          <Button color="error" onClick={handleConfirmLeave}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QuizDetail;
